import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  CustomFactor,
  CustomFactorDto,
  EmissionFactorCategory,
} from '../../types/factor';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ErrorComponent } from 'src/app/trip-calculator/components/error/error.component';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { LucideAngularModule } from 'lucide-angular';
import { TooltipIconComponent } from 'src/app/shared/tooltip-icon/tooltip-icon.component';

@Component({
  selector: 'app-factor-form-dialog',
  imports: [
    TranslocoPipe,
    ReactiveFormsModule,
    ErrorComponent,
    InputNumberModule,
    InputTextModule,
    DropdownModule,
    ButtonModule,
    DialogModule,
    LucideAngularModule,
    TooltipIconComponent,
  ],
  templateUrl: './factor-form-dialog.component.html',
})
export class FactorFormDialogComponent implements OnChanges, OnInit, OnDestroy {
  @Input() visible = false;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() factor?: CustomFactor;
  @Input() loading = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<CustomFactorDto>();

  private fb = inject(FormBuilder);
  translocoService = inject(TranslocoService);
  categoryOptions: { value: string; label: string }[] = [];
  private translationSub?: Subscription;

  factorForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(1)]],
    source: ['', [Validators.required, Validators.minLength(1)]],
    category: this.fb.control<EmissionFactorCategory | null>(null, [
      Validators.required,
    ]),
    valuePerKm: this.fb.control<number | null>(null, Validators.min(0)),
    valuePerH: this.fb.control<number | null>(null, Validators.min(0)),
    // speed: this.fb.control<number | null>(null, Validators.min(0)),
    valuePerNight: this.fb.control<number | null>(null, Validators.min(0)),
    uncertainty: this.fb.control<number | null>(null, Validators.min(0)),
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      if (this.mode === 'edit' && this.factor) {
        this.factorForm.reset(this.factor);
      } else {
        this.factorForm.reset();
      }
    }
  }

  ngOnInit() {
    this.translationSub = this.translocoService
      .selectTranslation()
      .subscribe(() => {
        this.categoryOptions = Object.values(EmissionFactorCategory).map(
          (category) => ({
            value: category,
            label: this.translocoService.translate(`calculator.${category}`),
          }),
        );
      });
  }

  ngOnDestroy() {
    this.translationSub?.unsubscribe();
  }
  closeDialog() {
    this.visible = false;
  }
  submit() {
    const data = this.factorForm.getRawValue();
    const factorDto: CustomFactorDto = {
      ...data,
      category: data.category!,
    };
    this.save.emit(factorDto);
  }
  onHide() {
    this.visibleChange.emit(false);
    this.factorForm.reset();
  }

  get isAccommodation(): boolean {
    return (
      this.factorForm.get('category')?.value ===
      EmissionFactorCategory.Accommodation
    );
  }

  get uncertaintyUrl(): string {
    const lang = this.translocoService.getActiveLang();
    return lang === 'fr'
      ? 'https://www.bilancarbone-methode.com/4-comptabilisation/4.4-methode-destimation-des-incertitudes/4.4.2-comment-les-determiner'
      : 'https://www.bilancarbone-methode.com/methode-bilan-carbone-r-en/4-accounting/4.4-method-for-estimating-uncertainties/4.4.2-how-to-determine-them';
  }
}
